import { create } from 'zustand';

/**
 * Global notification / toast store.
 *
 * This is the single source of truth for admin toasts. The toast panel
 * (`<NotificationSystem>`) is mounted once at the app root and subscribes to
 * this store via the `useNotifications()` hook, so ANY caller — a React
 * component, a custom hook, a background poller, or plain module code — can
 * raise a toast and have it render:
 *
 *   // From a component / hook (unchanged public API):
 *   const { addNotification } = useNotifications();
 *   addNotification('success', 'Saved!');
 *
 *   // From non-React code (a store action, a poller, an event handler):
 *   import useNotificationStore from '../stores/notificationStore';
 *   useNotificationStore.getState().addNotification('info', 'Job finished');
 *
 * Previously `useNotifications()` held its state in a per-caller `useState`
 * queue, so a toast pushed outside the component that renders the panel went
 * into a queue the panel never read and silently never rendered. Centralizing
 * the queue here fixes that.
 */

const VALID_TYPES = ['success', 'error', 'warning', 'info'];

/**
 * Normalize the three historical call shapes into a single notification object.
 *
 *   addNotification('error', 'message')            // canonical (type, message)
 *   addNotification('message', 'error')            // reversed (message, type)
 *   addNotification({ type, message, title, ... }) // object form
 *   addNotification('message only')                // single string -> message
 */
export function normalizeNotification(first, second) {
  // Object form.
  if (first && typeof first === 'object') {
    return { ...first };
  }

  // Two-string positional forms.
  if (typeof first === 'string' && typeof second === 'string') {
    // Canonical (type, message): first arg is a known type keyword.
    if (VALID_TYPES.includes(first)) {
      return { type: first, message: second };
    }
    // Reversed (message, type): second arg is a known type keyword.
    if (VALID_TYPES.includes(second)) {
      return { type: second, message: first };
    }
    // Neither is a known type — honor the documented (type, message) order.
    return { type: first, message: second };
  }

  // Single string -> treat as the message, default type applied below.
  if (typeof first === 'string') {
    return { message: first };
  }

  return {};
}

const useNotificationStore = create((set, get) => ({
  notifications: [],

  addNotification: (first, second) => {
    const normalized = normalizeNotification(first, second);
    const id = Date.now() + Math.random();
    const notification = {
      id,
      type: 'info',
      ...normalized,
    };

    set(state => ({ notifications: [...state.notifications, notification] }));

    // Auto-dismiss after the requested time (0 disables auto-dismiss).
    const timeout = notification.duration || 5000;
    if (timeout > 0) {
      setTimeout(() => {
        set(state => ({ notifications: state.notifications.filter(n => n.id !== id) }));
      }, timeout);
    }

    return id;
  },

  removeNotification: id => set(state => ({ notifications: state.notifications.filter(n => n.id !== id) })),

  showSuccess: (message, title = 'Success!', duration = 4000) =>
    get().addNotification({ type: 'success', message, title, duration }),

  showError: (message, title = 'Error', duration = 6000) =>
    get().addNotification({ type: 'error', message, title, duration }),

  showWarning: (message, title = 'Warning', duration = 5000) =>
    get().addNotification({ type: 'warning', message, title, duration }),

  showInfo: (message, title = 'Info', duration = 4000) =>
    get().addNotification({ type: 'info', message, title, duration }),
}));

export default useNotificationStore;
